// QuestionCard.tsx - Simplified JIT UI for self-intake questions
import React, { useState, useCallback } from 'react';
import {
  Card,
  CardContent,
  Stack,
  Typography,
  Button,
  Box,
  Chip,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField
} from '@mui/material';
import {
  QuestionMark as QuestionIcon,
  Check as CheckIcon
} from '@mui/icons-material';

interface QuestionOption {
  value: string;
  label: string;
}

interface QuestionData {
  questionId: string;
  questionText: string;
  questionType: 'multiple_choice' | 'yes_no' | 'text';
  options?: QuestionOption[];
  placeholder?: string;
  required?: boolean;
}

interface QuestionCardProps {
  questionData: QuestionData;
  compact?: boolean;
  onAnswerSelected?: (answer: string) => void;
}

export const QuestionCard: React.FC<QuestionCardProps> = ({
  questionData,
  compact = false,
  onAnswerSelected
}) => {
  const [selectedAnswer, setSelectedAnswer] = useState<string>('');
  const [customAnswer, setCustomAnswer] = useState<string>('');
  const [isAnswered, setIsAnswered] = useState(false);

  const handleAnswerSelection = useCallback(
    (answer: string) => {
      setSelectedAnswer(answer);
      setIsAnswered(true);
      onAnswerSelected?.(answer);
    },
    [onAnswerSelected]
  );

  const handleCustomAnswerSubmit = useCallback(() => {
    if (!customAnswer.trim()) return;
    setIsAnswered(true);
    onAnswerSelected?.(customAnswer.trim());
  }, [customAnswer, onAnswerSelected]);

  const handleKeyPress = useCallback(
    (event: React.KeyboardEvent) => {
      if (
        event.key === 'Enter' &&
        !event.shiftKey &&
        questionData.questionType === 'text'
      ) {
        event.preventDefault();
        handleCustomAnswerSubmit();
      }
    },
    [handleCustomAnswerSubmit, questionData.questionType]
  );

  const renderQuestionOptions = () => {
    if (isAnswered) {
      return (
        <Box sx={{ mt: 1, p: 1, bgcolor: 'success.light', borderRadius: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <CheckIcon fontSize="small" color="success" />
            <Typography variant="body2" color="success.dark">
              <strong>Selected:</strong>{' '}
              {questionData.questionType === 'text'
                ? customAnswer
                : selectedAnswer}
            </Typography>
          </Stack>
        </Box>
      );
    }

    switch (questionData.questionType) {
      case 'multiple_choice':
        return (
          <RadioGroup
            value={selectedAnswer}
            onChange={(e) => handleAnswerSelection(e.target.value)}
          >
            {questionData.options?.map((option) => (
              <FormControlLabel
                key={option.value}
                value={option.value}
                control={<Radio size={compact ? 'small' : 'medium'} />}
                label={
                  <Typography variant={compact ? 'body2' : 'body1'}>
                    {option.label}
                  </Typography>
                }
                sx={{
                  '& .MuiFormControlLabel-label': {
                    fontSize: compact ? '0.875rem' : undefined
                  }
                }}
              />
            ))}
          </RadioGroup>
        );

      case 'yes_no':
        return (
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <Button
              variant="contained"
              color="success"
              size={compact ? 'small' : 'medium'}
              onClick={() => handleAnswerSelection('Yes')}
              sx={{ minWidth: compact ? 60 : 80 }}
            >
              Yes
            </Button>
            <Button
              variant="contained"
              color="error"
              size={compact ? 'small' : 'medium'}
              onClick={() => handleAnswerSelection('No')}
              sx={{ minWidth: compact ? 60 : 80 }}
            >
              No
            </Button>
          </Stack>
        );

      case 'text':
        return (
          <Stack spacing={1} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              multiline
              maxRows={4}
              value={customAnswer}
              onChange={(e) => setCustomAnswer(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={
                questionData.placeholder || 'Type your answer here...'
              }
              size={compact ? 'small' : 'medium'}
            />
            <Button
              variant="contained"
              size="small"
              onClick={handleCustomAnswerSubmit}
              disabled={!customAnswer.trim()}
              sx={{ alignSelf: 'flex-start' }}
            >
              Select This Answer
            </Button>
          </Stack>
        );

      default:
        return null;
    }
  };

  const isCompact = !!compact;

  return (
    <Card
      variant="outlined"
      sx={{
        my: 1,
        borderLeft: 4,
        borderLeftColor: isAnswered ? 'success.main' : 'primary.main',
        width: '100%'
      }}
    >
      <CardContent sx={{ py: isCompact ? 1 : 1.5, px: isCompact ? 1.25 : 2 }}>
        <Stack spacing={isCompact ? 0.75 : 1}>
          {/* Header */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ flexWrap: 'wrap' }}
          >
            <QuestionIcon
              fontSize="small"
              color={isAnswered ? 'success' : 'primary'}
            />
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 'bold',
                flex: 1,
                whiteSpace: 'normal',
                wordBreak: 'break-word'
              }}
            >
              Quick Answer
            </Typography>
            <Chip
              size="small"
              label={questionData.questionType.replace('_', ' ').toUpperCase()}
              color={isAnswered ? 'success' : 'primary'}
              sx={{
                fontSize: isCompact ? '0.65rem' : '0.7rem',
                height: isCompact ? 18 : 20,
                px: isCompact ? 0.75 : 1
              }}
            />
            {isAnswered && (
              <Chip
                size="small"
                label="SELECTED"
                color="success"
                sx={{
                  fontSize: isCompact ? '0.65rem' : '0.7rem',
                  height: isCompact ? 18 : 20,
                  px: isCompact ? 0.75 : 1
                }}
              />
            )}
          </Stack>

          {/* Question Text */}
          <Typography
            variant="body1"
            sx={{
              fontSize: isCompact ? '0.9rem' : undefined,
              fontWeight: 500
            }}
          >
            {questionData.questionText}
          </Typography>

          {/* Question Options/Input */}
          {renderQuestionOptions()}
        </Stack>
      </CardContent>
    </Card>
  );
};
